import { useCallback, useRef, useState } from 'react';
import { Delete as DeleteIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  ClickAwayListener,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import { aiGradientTextSx } from 'common/constants/ai';
import GradientDivider from '../../../../Documents/components/SuggestedDocumentReference/GradientDivider';
import {
  actionButtonSx,
  commentaryItemSx,
  commentaryTitleSx,
  declineIconSx,
  expandedButtonsContainerSx,
  expandedHeaderSx,
  expandedIconSx,
  expandedPaperSx,
  expandedTitleSx,
} from '../../../../Documents/components/SuggestedDocumentReference/SuggestedDocumentReference.styles';
import { AIFieldMetadata } from '../captable-table/CaptableTable.props';

const CLOSE_DELAY = 100;

interface AISecurityFindingPopoverProps {
  fieldMetadata: AIFieldMetadata;
  accuracyConfidence?: string | null;
  fieldLabel: string;
  onRemove?: () => void;
  children: (props: {
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
  }) => React.ReactNode;
}

const AISecurityFindingPopover = ({
  fieldMetadata,
  accuracyConfidence,
  fieldLabel,
  onRemove,
  children,
}: AISecurityFindingPopoverProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
  }, []);

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    if (!event.ctrlKey && !event.metaKey) return;

    event.preventDefault();
    event.stopPropagation();
    clearCloseTimeout();
    setAnchorEl(prev => (prev ? null : event.currentTarget));
  }, [clearCloseTimeout]);

  const handleClose = useCallback(() => {
    clearCloseTimeout();
    setAnchorEl(null);
  }, [clearCloseTimeout]);

  const handleRemove = useCallback(() => {
    handleClose();
    onRemove?.();
  }, [handleClose, onRemove]);

  const handlePopperMouseEnter = useCallback(() => {
    clearCloseTimeout();
  }, [clearCloseTimeout]);

  const handlePopperMouseLeave = useCallback(() => {
    closeTimeout.current = setTimeout(() => {
      setAnchorEl(null);
    }, CLOSE_DELAY);
  }, []);

  const open = Boolean(anchorEl);
  const {confidence} = fieldMetadata;
  const commentary = fieldMetadata.commentary ?? [];
  const overallConfidence = accuracyConfidence ? parseFloat(accuracyConfidence) : null;

  return (
    <>
      {children({ onClick: handleClick })}

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="top"
        modifiers={[
          { name: 'offset', options: { offset: [0, 4] } },
          { name: 'flip', options: { fallbackPlacements: ['bottom-start', 'top-end'] } },
        ]}
        sx={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            onMouseEnter={handlePopperMouseEnter}
            onMouseLeave={handlePopperMouseLeave}
            onMouseDown={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}
            elevation={4}
            sx={expandedPaperSx}
          >
            <Box sx={expandedHeaderSx}>
              <Box component="img" src="/images/scalar_ai.png" alt="Scalar AI" sx={expandedIconSx} />
              <Box>
                <Typography sx={expandedTitleSx}>
                  AI Finding
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {fieldLabel}
                </Typography>
              </Box>
            </Box>

            <GradientDivider />

            {confidence != null && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
                  Field confidence:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.78rem',
                    ...(aiGradientTextSx as Record<string, unknown>),
                  }}
                >
                  {Math.round(confidence * 100)}%
                </Typography>
              </Box>
            )}

            {overallConfidence != null && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
                  Overall accuracy:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.78rem' }}>
                  {Math.round(overallConfidence * 100)}%
                </Typography>
              </Box>
            )}

            {commentary.length > 0 && (
              <>
                <GradientDivider />
                <Box>
                  <Typography variant="body2" sx={commentaryTitleSx}>
                    Commentary
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
                    {commentary.map((note, index) => (
                      <Typography
                        key={index}
                        component="li"
                        variant="body2"
                        color="text.secondary"
                        sx={commentaryItemSx}
                      >
                        {note}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </>
            )}

            {onRemove && (
              <>
                <GradientDivider />
                <Box sx={expandedButtonsContainerSx}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleRemove}
                    endIcon={<DeleteIcon sx={declineIconSx} />}
                    sx={actionButtonSx}
                  >
                    Remove Finding
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default AISecurityFindingPopover;
